import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { SortState } from '@/types/table';

// Helper to add search values to URL params
function addSearchValuesToParams(params: URLSearchParams, searchValues: Record<string, string>) {
  Object.entries(searchValues).forEach(([fieldPath, value]) => {
    if (value && value.trim()) {
      if (fieldPath === 'case_id' || fieldPath === 'reference_id') {
        params.set(fieldPath, value);
      } else {
        params.set(`search_${fieldPath}`, value);
      }
    }
  });
}

// Helper to parse search values from URL params
function parseSearchValuesFromParams(searchParams: URLSearchParams): Record<string, string> {
  const searchValues: Record<string, string> = {};
  const caseId = searchParams.get('case_id');
  const referenceId = searchParams.get('reference_id');
  if (caseId) searchValues.case_id = caseId;
  if (referenceId) searchValues.reference_id = referenceId;
  searchParams.forEach((value, key) => {
    if (key.startsWith('search_')) {
      searchValues[key.replace('search_', '')] = value;
    }
  });
  return searchValues;
}

// Helper to parse sort state from URL
function parseSortState(sortParam: string | null): SortState[] {
  if (!sortParam) return [];
  try {
    const parsed = JSON.parse(sortParam);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// Helper to set a comma-joined list param, skipping empty lists.
function setCsvParam(params: URLSearchParams, key: string, values?: string[]) {
  if (values && values.length > 0) {
    params.set(key, values.join(','));
  }
}

// Helper to add pagination to params
function addPaginationToParams(
  params: URLSearchParams,
  pagination?: { page: number; cursor?: string },
) {
  if (pagination && pagination.page > 1) {
    params.set('page', pagination.page.toString());
    if (pagination.cursor) params.set('cursor', pagination.cursor);
  }
}

// Helper to build URL params from filter state
function buildFilterParams(
  activeQuickFilter: string,
  searchValues: Record<string, string>,
  assignedFilter: string[],
  dateFrom?: string,
  dateTo?: string,
  sortState?: SortState[],
  pagination?: { page: number; cursor?: string },
  workflowId?: string,
  manufacturers?: string[],
): URLSearchParams {
  const params = new URLSearchParams();

  if (activeQuickFilter && activeQuickFilter !== 'all') {
    params.set('filter', activeQuickFilter);
  }

  addSearchValuesToParams(params, searchValues);

  setCsvParam(params, 'assigned', assignedFilter);

  if (dateFrom) params.set('date_from', dateFrom);
  if (dateTo) params.set('date_to', dateTo);
  if (sortState && sortState.length > 0) params.set('sort', JSON.stringify(sortState));

  addPaginationToParams(params, pagination);

  // Add workflow parameter
  if (workflowId) {
    params.set('workflow_id', workflowId);
  }

  // Manufacturer (PQC workflow) multi-select — only the org-wide Tier-1 page
  // passes this; per-program callers omit it, leaving the param unset.
  setCsvParam(params, 'manufacturers', manufacturers);

  return params;
}

/**
 * Custom hook to sync filter state with URL parameters
 * Handles reading initial values from URL and updating URL when filters change
 */
export function useFilterParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Function to sync filters with URL
  const syncFiltersToUrl = useCallback(
    (
      activeQuickFilter: string,
      searchValues: Record<string, string>,
      assignedFilter: string[],
      dateFrom?: string,
      dateTo?: string,
      sortState?: SortState[],
      pagination?: { page: number; cursor?: string },
      shouldReplace = false,
      workflowId?: string,
      manufacturers?: string[],
    ) => {
      const params = buildFilterParams(
        activeQuickFilter,
        searchValues,
        assignedFilter,
        dateFrom,
        dateTo,
        sortState,
        pagination,
        workflowId,
        manufacturers,
      );
      setSearchParams(params, { replace: shouldReplace });
    },
    [setSearchParams],
  );

  // Helper function to get initial values from URL
  const getInitialValues = () => {
    const assignedParam = searchParams.get('assigned');
    const pageParam = searchParams.get('page');
    const cursorParam = searchParams.get('cursor');
    const manufacturersParam = searchParams.get('manufacturers');

    return {
      filter: searchParams.get('filter') || 'all',
      searchValues: parseSearchValuesFromParams(searchParams),
      assigned: assignedParam ? assignedParam.split(',').filter(Boolean) : [],
      dateFrom: searchParams.get('date_from') || '',
      dateTo: searchParams.get('date_to') || '',
      sortState: parseSortState(searchParams.get('sort')),
      page: pageParam ? parseInt(pageParam, 10) : 1,
      cursor: cursorParam || undefined,
      workflow: searchParams.get('workflow_id') || undefined,
      manufacturers: manufacturersParam ? manufacturersParam.split(',').filter(Boolean) : [],
    };
  };

  // Helper function to clear all filters from URL
  const clearUrlFilters = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  return {
    searchParams,
    syncFiltersToUrl,
    getInitialValues,
    clearUrlFilters,
  };
}

/**
 * Helper function to validate if a filter exists in config
 * Can be used when reading from URL to ensure valid filters
 */
export function isValidFilter(filterId: string, validFilters: Array<{ id: string }>): boolean {
  return validFilters.some(f => f.id === filterId);
}

/**
 * Helper function to validate and filter search values against allowed field paths
 * Prevents URL parameter injection by only accepting whitelisted fields
 *
 * @param searchValues - Search values from URL parameters
 * @param allowedFieldPaths - Array of allowed field paths (from column config)
 * @returns Filtered search values containing only allowed field paths
 */
export function validateSearchValues(
  searchValues: Record<string, string>,
  allowedFieldPaths: string[],
): Record<string, string> {
  const validated: Record<string, string> = {};

  // Always allow standard fields
  if (searchValues.case_id) {
    validated.case_id = searchValues.case_id;
  }
  if (searchValues.reference_id) {
    validated.reference_id = searchValues.reference_id;
  }

  // Validate custom field paths against allowed list
  Object.entries(searchValues).forEach(([fieldPath, value]) => {
    if (
      fieldPath !== 'case_id' &&
      fieldPath !== 'reference_id' &&
      allowedFieldPaths.includes(fieldPath)
    ) {
      validated[fieldPath] = value;
    }
  });

  return validated;
}

/**
 * Helper function to validate sort state against allowed field paths
 * Prevents URL parameter injection by only accepting whitelisted fields
 *
 * @param sortState - Sort state from URL parameters
 * @param allowedFieldPaths - Array of allowed field paths (from column config)
 * @returns Filtered sort state containing only allowed field paths
 */
export function validateSortState(
  sortState: SortState[],
  allowedFieldPaths: string[],
): SortState[] {
  // Standard fields that are always allowed
  const standardFields = ['case_id', 'reference_id', 'created_at', 'completed_at', 'state'];

  // Filter sort state to only include allowed fields
  return sortState.filter(sort => {
    const isStandardField = standardFields.includes(sort.field);
    const isAllowedCustomField = allowedFieldPaths.includes(sort.field);
    return isStandardField || isAllowedCustomField;
  });
}
